import { supabase } from '@/lib/supabase'
import type { AircraftCatalog, CatalogEntry } from '../engine/types'

interface AircraftTypeRow {
  id: string
  type_code: string | null
  model_url: string | null
  model_settings: {
    models?: { lod1?: { url?: string } }
    filePath?: string
    [k: string]: unknown
  } | null
  length: number | null
  wingspan: number | null
  height: number | null
  tail_height: number | null
  category: string | null
}

/**
 * Load the org's aircraft types into a typeCode → CatalogEntry map.
 *
 * Mirrors `transformAllocationTo3D`'s model-URL resolution so event aircraft
 * render identically to real allocations. Types lacking a footprint (wingspan +
 * length) are skipped — the placement geometry needs those.
 */
export async function loadAircraftCatalog(): Promise<AircraftCatalog> {
  const { data, error } = await supabase
    .from('aircraft_types')
    .select('id, type_code, model_url, model_settings, length, wingspan, height, tail_height, category')
    .eq('is_active', true)

  if (error) throw error

  const catalog: AircraftCatalog = new Map()

  for (const row of (data ?? []) as AircraftTypeRow[]) {
    if (!row.type_code) continue
    if (!row.length || !row.wingspan || !row.height) continue // footprint + render need dims

    const modelSettings = row.model_settings ?? {}
    const modelUrl = row.model_url || modelSettings.models?.lod1?.url || modelSettings.filePath

    const entry: CatalogEntry = {
      typeId: row.id,
      typeCode: row.type_code,
      aircraftType: {
        id: row.id,
        typeCode: row.type_code,
        modelSettings: { ...modelSettings, filePath: modelUrl || undefined },
      },
      dimensions: {
        length: row.length,
        wingspan: row.wingspan,
        height: row.height,
        tailHeight: row.tail_height ?? null,
        category: row.category ?? null,
        gearType: null,
      },
      footprint: { wingspan: row.wingspan, length: row.length },
      category: row.category ?? null,
      // nose_gear_from_nose isn't in the schema yet; tugNoseOffset falls back to
      // a length-based estimate. Reserved hook for when gear geometry is added.
    }

    catalog.set(row.type_code, entry)
  }

  return catalog
}
